<template>
  <dialog-base
    scrollable
    v-bind="$props"
    :key="renderKey"
    header="Link to appointment"
    width="820"
    max-width="100%"
    @close="close">
    <template #body>
      <div>
        {{
          t(
            'assetVisit.dialogs.linkAppointmentDialog.messages.chooseAnExistingAppointmentToLinkThisArrivalToTheAppointmentMustBeInTheRequestedOrScheduledStatuses'
          )
        }}
      </div>
      <em>
        {{
          t(
            'assetVisit.dialogs.linkAppointmentDialog.messages.searchByConfirmationNumberReferenceNumberCompanyNameDockNameLoadTypeNameOrStatus'
          )
        }}
      </em>

      <v-container class="rounded-box">
        <v-row align="center" class="mb-2 header-row">
          <v-col cols="5">
            <v-text-field
              class="box-text-field prepend-icon"
              :placeholder="
                t('assetVisit.dialogs.linkAppointmentDialog.placeholders.searchAppointments')
              "
              hide-details
              persistent-placeholder
              v-model="searchStr">
              <template #append>
                <v-icon size="small">mdi-magnify</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="3">
            <span class="text--color-text-secondary">
              <strong>{{ totalAppointments }}</strong>
              {{ t('global.specialtyTerms.appointments', totalAppointments) }}
            </span>
          </v-col>
        </v-row>
        <v-data-table
          @click:row="handleRowClick"
          :items="appointments"
          :headers="headers"
          :hover="true"
          fixed-header
          class="mt-4"
          height="50vh"
          :footer-props="{ 'items-per-page-options': [], 'items-per-page-text': '' }">
          <template #item.start="{ item }">{{ getAppointmentStart(item.start) }}</template>
          <template #item.status="{ item }">
            <v-chip
              size="small"
              :color="item.status.toLowerCase()"
              class="is-uppercase font-weight-bold"
              :class="{
                'text--requested-darken3': isRequested(item),
                'text--color-neutral-0': isScheduled(item)
              }">
              {{ t(`global.appointmentStatuses.${item.status}`) }}
            </v-chip>
          </template>
        </v-data-table>
      </v-container>

      <v-dialog
        width="820"
        max-width="100%"
        v-model="shouldShowConfirmLinkDialog"
        v-if="appointmentToLink">
        <v-card>
          <v-card-title class="dialog-title">
            <span class="text-h5">
              {{ t('assetVisit.dialogs.linkAppointmentDialog.headers.confirmLink') }}
            </span>
            <v-btn icon @click="closeConfirmLinkDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <span>
              {{
                t(
                  'assetVisit.dialogs.linkAppointmentDialog.messages.theArrivalWillBeMarkedAsPlannedTheAssetAndTheAppointmentWillBeMarkedAsArrived'
                )
              }}
            </span>
            <div class="rounded-box my-6 pa-4">
              <span class="font-size-medium font-weight-bold">
                {{ t('assetVisit.dialogs.linkAppointmentDialog.headers.appointmentDetails') }}
              </span>
              <key-value-list variant="grid">
                <key-value
                  :label="t('assetVisit.dialogs.linkAppointmentDialog.labels.conf')"
                  :value="appointmentToLink.confirmationNumber" />
                <key-value
                  :label="t('assetVisit.dialogs.linkAppointmentDialog.labels.startDate')"
                  :value="appointmentStartDate" />
                <key-value
                  :label="t('assetVisit.dialogs.linkAppointmentDialog.labels.startTime')"
                  :value="appointmentStartTime" />
                <key-value
                  :label="t('assetVisit.dialogs.linkAppointmentDialog.labels.carrierCompany')"
                  :value="appointmentToLink.user.company.name" />
                <key-value
                  :label="t('global.specialtyTerms.docks')"
                  :value="appointmentToLink.dock.name" />
                <key-value
                  :label="t('assetVisit.dialogs.linkAppointmentDialog.labels.direction')"
                  :value="appointmentToLink.loadType.direction" />
              </key-value-list>
            </div>

            <div class="rounded-box my-6 pa-4" v-if="appointmentType === 'visit'">
              <asset-visit-form-data
                variant="grid"
                :phone-title="t('global.basicTerms.phone')"
                :company-title="t('global.specialtyTerms.companies')"
                :asset-visit="assetToLink"
                :company="assetToLink.company || { name: assetToLink.companyHint }" />
            </div>
          </v-card-text>
          <v-card-actions class="py-4">
            <outline-button before-icon="arrow-left" @click="closeConfirmLinkDialog">
              {{ t('assetVisit.dialogs.linkAppointmentDialog.buttons.backToAppointmentsList') }}
            </outline-button>
            <v-spacer></v-spacer>
            <primary-button
              @click="
                () =>
                  appointmentType === 'visit'
                    ? linkUnplannedCheckinToAppointment()
                    : linkAssetContainerToAppointment()
              ">
              {{ t('assetVisit.dialogs.linkAppointmentDialog.buttons.confirmLink') }}
            </primary-button>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </dialog-base>
</template>
<script>
import {
  useRenderKey,
  useDialogBase,
  useRouter,
  useDebouncedInput,
  dialogBaseProps
} from '@/composables';
import { DateTime } from 'luxon';
import appointmentService from '@satellite/services/appointment-service';
import checkinService from '@satellite/services/checkin-service';
import {
  AppointmentStatus,
  formatDateTimeWithMilitarySupport,
  LuxonDateTimeFormats,
  isRequested,
  isScheduled,
  AppointmentType,
  pluralize,
  searchStringToQuery,
  getTranslatedRefNumberDisplayName
} from '@nova/core';
import { ref, watch, computed } from 'vue';
import { debounce } from 'lodash';

import KeyValueList from '@satellite/components/elements/key_value/KeyValueList.vue';
import KeyValue from '@satellite/components/elements/key_value/KeyValue.vue';
import { useI18n } from 'vue-i18n';

export default {
  components: {
    KeyValueList,
    KeyValue
  },
  props: {
    ...dialogBaseProps,
    warehouse: {
      type: Object,
      required: true
    },
    assetToLink: {
      type: Object,
      required: true
    },
    /*
     * When it is drop or pickup type
     * we will be linking to the assetContainer,
     * otherwise we will be linking to the assetVisit
     */
    appointmentType: {
      type: String,
      default: 'visit',
      validator: value => ['drop', 'pickup', 'visit'].includes(value)
    },
    isMilitaryTimeEnabled: {
      type: Boolean,
      required: true
    },
    referenceNumberSettings: {
      type: Object,
      required: true
    },
    /*
     * If true, the form will follow to the appointment details page after creating an appointment
     */
    followToAppointmentDetails: {
      type: Boolean,
      default: true
    },
    /*
     * If true, the dialog will show the arrived appointments and linked appointments
     */
    allowLinkToArrivedAppointments: {
      type: Boolean,
      default: false
    },
    /*
     * The number of days to show the available appointments from its start date
     */
    appointmentDaysThreshold: {
      type: Number,
      default: 2
    }
  },
  emits: ['linked', 'close', 'confirm'],
  setup(props, context) {
    const { t } = useI18n();
    const router = useRouter();
    const { renderKey } = useRenderKey();

    const onClose = () => {
      context.emit('close');
    };

    const onDismiss = () => context.emit('close');

    const onConfirm = () => context.emit('confirm');

    const { close } = useDialogBase({
      onClose,
      onDismiss,
      onConfirm
    });

    const shouldShowConfirmLinkDialog = ref(false);

    const { value: searchStr } = useDebouncedInput({
      initialValue: '',
      onChange: () => {
        fetchAppointments();
      }
    });

    const appointmentToLink = ref(null);

    const appointments = ref([]);
    const totalAppointments = ref(0);

    const appointmentStartDate = computed(() =>
      DateTime.fromISO(appointmentToLink.value?.start, { zone: props.warehouse.timezone }).toFormat(
        LuxonDateTimeFormats.LongDateShortMonth
      )
    );

    const appointmentStartTime = computed(() =>
      formatDateTimeWithMilitarySupport(
        appointmentToLink.value?.start,
        props.warehouse.timezone,
        LuxonDateTimeFormats.Extended12HrTimeAMPMWithAbbreviatedNamedOffset,
        props.isMilitaryTimeEnabled,
        LuxonDateTimeFormats.Extended24HrTimeWithAbbreviatedNamedOffset
      )
    );

    // Appointments table
    const headers = [
      {
        title: t('assetVisit.dialogs.linkAppointmentDialog.labels.conf'),
        align: 'start',
        value: 'confirmationNumber',
        sortable: false
      },
      {
        title: getTranslatedRefNumberDisplayName(props.referenceNumberSettings?.displayName),
        value: 'refNumber',
        sortable: false
      },
      {
        title: t('assetVisit.dialogs.linkAppointmentDialog.labels.startTime'),
        width: 148,
        value: 'start',
        sortable: false
      },
      { title: t('global.specialtyTerms.carriers'), value: 'user.company.name', sortable: false },
      { title: t('global.specialtyTerms.docks'), value: 'dock.name', sortable: false },
      { title: t('global.specialtyTerms.loadTypes'), value: 'loadType.name', sortable: false },
      { title: t('global.specialtyTerms.status'), value: 'status', sortable: false }
    ];

    function getAppointmentStart(startUTC) {
      return formatDateTimeWithMilitarySupport(
        startUTC,
        props.warehouse.timezone,
        LuxonDateTimeFormats.LongDateTimeShortDayShortMonthWithAbbreviatedNamedOffset,
        props.isMilitaryTimeEnabled,
        LuxonDateTimeFormats.LongDateTime24ShortDayShortMonthWithAbbreviatedNamedOffset
      );
    }
    // End of appointments table

    function handleRowClick($event, { item }) {
      appointmentToLink.value = item;
      shouldShowConfirmLinkDialog.value = true;
    }

    function closeConfirmLinkDialog() {
      appointmentToLink.value = null;
      shouldShowConfirmLinkDialog.value = false;
    }

    async function fetchAppointments() {
      const now = DateTime.now();
      const query = {
        s: {
          $and: [
            { 'dock.warehouse.id': props.warehouse?.id },
            {
              status: {
                $in: [
                  AppointmentStatus.Requested,
                  AppointmentStatus.Scheduled,
                  ...(props.allowLinkToArrivedAppointments ? [AppointmentStatus.Arrived] : [])
                ]
              }
            },
            {
              start: {
                $between: [
                  now.minus({ days: Number(props.appointmentDaysThreshold) }).toISO(),
                  now.plus({ days: Number(props.appointmentDaysThreshold) }).toISO()
                ]
              }
            },
            { type: AppointmentType.Standard },
            ...(props.allowLinkToArrivedAppointments ? [] : [{ 'assetVisit.id': { $eq: null } }])
          ]
        }
      };

      if (searchStr.value) {
        query.s.$and.push({
          $or: searchStringToQuery(searchStr.value, {
            searchableFields: [
              'confirmationNumber',
              'refNumber',
              'user.company.name',
              'dock.name',
              'loadType.name',
              'status'
            ],
            searchFieldsToSplit: []
          })
        });
      }

      const res = await appointmentService.getAppointments(query, {
        includeMetaData: true,
        joins: [
          'dock||name,id',
          'dock.warehouse||id',
          'loadType||name,direction',
          'user||id',
          'user.company||name',
          'assetVisit||id'
        ],
        fields: ['confirmationNumber', 'refNumber', 'start', 'status']
      });

      totalAppointments.value = res?.data?.total;
      appointments.value = res?.data?.data;
    }

    async function linkAssetContainerToAppointment() {
      context.emit('linked', {
        appointment: appointmentToLink.value,
        appointmentType: props.appointmentType,
        assetContainerId: props.assetToLink.id
      });

      closeConfirmLinkDialog();
      close();
    }

    async function linkUnplannedCheckinToAppointment() {
      await checkinService.linkUnplannedCheckinToAppointment({
        appointmentId: appointmentToLink.value.id,
        companyId: appointmentToLink.value.user.company.id,
        assetVisitId: props.assetToLink.id,
        orgId: props.warehouse.orgId,
        isPlanned: true
      });

      if (props.followToAppointmentDetails) {
        await router.replace(`/appointments?appointmentId=${appointmentToLink.value.id}`);
      }

      context.emit('linked', {
        appointmentId: appointmentToLink.value.id,
        id: props.assetToLink.id
      });

      closeConfirmLinkDialog();
      close();
    }

    const debounceSearchStr = debounce(async () => {
      await fetchAppointments();
    }, 350);

    watch(
      () => props.showDialog,
      async value => {
        if (value) {
          await fetchAppointments();
        }
      },
      { immediate: true }
    );

    watch(searchStr, debounceSearchStr);

    return {
      t,
      appointmentStartDate,
      appointmentStartTime,
      appointmentToLink,
      appointments,
      close,
      closeConfirmLinkDialog,
      getAppointmentStart,
      handleRowClick,
      headers,
      isRequested,
      isScheduled,
      linkUnplannedCheckinToAppointment,
      linkAssetContainerToAppointment,
      renderKey,
      searchStr,
      shouldShowConfirmLinkDialog,
      totalAppointments,
      pluralize
    };
  }
};
</script>

<style scoped lang="scss">
.container {
  padding-bottom: 0;
  margin-top: 1rem;
}

:deep(.v-data-footer) {
  background-color: $color-neutral-0;
  position: sticky;
  bottom: -20;
  margin-left: -12px;
  width: calc(100% + 24px);
  justify-content: flex-end;
  padding: 12px 0;
}

:deep(td) {
  &:hover {
    cursor: pointer;
  }
}

.header-row {
  border-bottom: 1px solid $color-line-border;
  padding: 6px;
}
</style>
